import { actionTypes } from '../actions/types';
import { errorNames } from '../util/mappings/errorName';

type AppActions = import('../actions/types').AppActions;

export interface Applicant {
    ssn?: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    employmentConditionCode: string;
    livingConditionCode: string;
    rentalFee: number | undefined;
    grossIncome: number | undefined;
    totalDebt: number | undefined;
    tradeIn: boolean;
    tradeInDebt: number | undefined;
}

export interface ApplicationReducer {
    // Applicants
    hasCoApplicant: boolean;
    hasConfirmedAddress: boolean;
    creditFrozen: boolean;
    mainApplicant: Applicant;
    coApplicant: Applicant;
    applicationErrorNotFound: boolean;
}

export const initialState: ApplicationReducer = {
    // Applicants
    hasCoApplicant: false,
    hasConfirmedAddress: false,
    creditFrozen: false,
    mainApplicant: {
        ssn: undefined,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        employmentConditionCode: '',
        livingConditionCode: '',
        rentalFee: undefined,
        grossIncome: undefined,
        totalDebt: undefined,
        tradeIn: false,
        tradeInDebt: undefined,
    },
    coApplicant: {
        ssn: undefined,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        employmentConditionCode: '',
        livingConditionCode: '',
        rentalFee: undefined,
        grossIncome: undefined,
        totalDebt: undefined,
        tradeIn: false,
        tradeInDebt: undefined,
    },
    applicationErrorNotFound: false,
};

export const applicationReducer = (state = initialState, action: AppActions = {} as AppActions): ApplicationReducer => {
    switch (action.type) {
        case actionTypes.FETCH_USER_CONTEXT_FULFILLED: {
            const { ssn, firstName, lastName, email, phone } = action.payload;
            return {
                ...state,
                mainApplicant: {
                    ...state.mainApplicant,
                    ssn,
                    firstName: firstName || '',
                    lastName: lastName || '',
                    email: email || state.mainApplicant.email,
                    phone: phone || state.mainApplicant.phone,
                },
            };
        }

        case actionTypes.FORM_APPLICATION_MAIN_APPLICANT: {
            const { rentalFee, tradeIn, tradeInDebt, grossIncome } = action.payload;
            return {
                ...state,
                mainApplicant: {
                    ...state.mainApplicant,
                    ...action.payload,
                    grossIncome: grossIncome !== undefined ? grossIncome : 0,
                    rentalFee: rentalFee !== undefined ? rentalFee : 0,
                    tradeIn,
                    tradeInDebt: tradeInDebt !== undefined ? tradeInDebt : 0,
                },
            };
        }

        case actionTypes.TOGGLE_CO_APPLICANT: {
            return {
                ...state,
                hasCoApplicant: action.payload,
            };
        }

        case actionTypes.FORM_APPLICATION_CO_APPLICANT: {
            const {
                confirmAddress,
                ssn,
                email,
                phone,
                livingConditionCode,
                rentalFee,
                employmentConditionCode,
                grossIncome,
                totalDebt,
                tradeIn,
                tradeInDebt,
            } = action.payload;
            return {
                ...state,
                hasConfirmedAddress: confirmAddress,
                coApplicant: {
                    ...state.coApplicant,
                    ssn,
                    email,
                    phone,
                    livingConditionCode,
                    rentalFee: rentalFee !== undefined ? rentalFee : 0,
                    employmentConditionCode,
                    grossIncome: grossIncome !== undefined ? grossIncome : 0,
                    totalDebt,
                    tradeIn,
                    tradeInDebt: tradeInDebt !== undefined ? tradeInDebt : 0,
                },
            };
        }

        case actionTypes.FETCH_APPLICATION_FULFILLED:
        case actionTypes.SEND_APPLICATION_FULFILLED: {
            const creditFrozen = action.payload?.creditFrozen;
            return {
                ...state,
                creditFrozen: creditFrozen === 'YES',
                applicationErrorNotFound: false,
            };
        }

        case actionTypes.FETCH_APPLICATION_FAILED:
        case actionTypes.SEND_APPLICATION_FAILED: {
            const { errorName } = action.payload;
            return {
                ...state,
                applicationErrorNotFound:
                    errorName === errorNames.applicationNotFound || state.applicationErrorNotFound,
            };
        }

        default:
            return state;
    }
};
